<template>
  <div v-if="authStore.lockedOut" class="session-locked-overlay">
    <div class="session-locked-backdrop" />
    <q-card class="session-locked-card q-pa-xl column items-center q-gutter-md" flat bordered>
      <q-icon name="lock" size="3rem" color="grey-6" />
      <div class="text-h6">Sitzung abgelaufen</div>
      <div class="text-body2 text-center text-grey-7">
        Deine Sitzung ist abgelaufen. Bitte melde dich an, um fortzufahren.
      </div>
      <q-btn color="primary" label="Login" size="md" :loading="checking" @click="handleLogin()" />
    </q-card>
  </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref, watch } from "vue"
import { useAuthStore } from "src/stores/auth"

const AUTH_TOKEN_KEY = "bx-ops-auth-token"

const authStore = useAuthStore()
const checking = ref(false)

let autoCheckInterval: ReturnType<typeof setInterval> | null = null

function handleStorageChange(event: StorageEvent) {
  if (event.key !== AUTH_TOKEN_KEY) return
  if (event.newValue) {
    // Another tab saved a new token (e.g. returned from OIDC) — restore session here too
    void authStore.tryRestoreSession()
  } else {
    // Another tab cleared the token (locked out) — mirror that here
    authStore.lockOut()
  }
}

onMounted(() => {
  window.addEventListener("storage", handleStorageChange)
})

watch(
  () => authStore.lockedOut,
  (locked) => {
    if (locked) {
      autoCheckInterval = setInterval(() => {
        void authStore.tryRestoreSession()
      }, 30_000)
    } else {
      if (autoCheckInterval !== null) {
        clearInterval(autoCheckInterval)
        autoCheckInterval = null
      }
    }
  },
)

async function handleLogin() {
  checking.value = true
  try {
    const restored = await authStore.tryRestoreSession()
    if (!restored) {
      authStore.redirectToLogin()
    }
  } finally {
    checking.value = false
  }
}

onUnmounted(() => {
  window.removeEventListener("storage", handleStorageChange)
  if (autoCheckInterval !== null) {
    clearInterval(autoCheckInterval)
  }
})
</script>

<style scoped>
.session-locked-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}

.session-locked-backdrop {
  position: absolute;
  inset: 0;
  backdrop-filter: blur(6px);
  background: rgba(0, 0, 0, 0.35);
}

.session-locked-card {
  position: relative;
  z-index: 1;
  min-width: min(360px, 100vw - 48px);
  text-align: center;
}
</style>
